import React, { useMemo } from 'react';
import Plyr from 'plyr-react';
import { ButtonLink } from './shared/ButtonLink';
import 'plyr-react/plyr.css';
import type { MediaTextComponent as MediaTextComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getCroppedImageRenderProps } from './ComponentEditModal/shared/cropUtils';
import { getFontFamily } from '../utils/fontLoader';
import {
  EMBED_ASPECT_RATIO,
  EMBED_ASPECT_RATIO_CSS,
  getEmbedSrc,
  isEmbedProvider,
} from '../utils/videoEmbed';

interface MediaTextComponentProps {
  component: MediaTextComponentType;
}

export const MediaTextComponent: React.FC<MediaTextComponentProps> = ({ component }) => {
  const { data: rawData } = component;

  // Defensive defaults for AI-generated data that may omit optional fields
  const data = {
    ...rawData,
    buttons: rawData.buttons || [],
    padding: rawData.padding || { top: 60, bottom: 60, left: 20, right: 20 },
    spacing: rawData.spacing || 'normal',
    media: {
      ...rawData.media,
      type: rawData.media?.type || 'image',
      url: rawData.media?.url || '',
    },
  } as typeof rawData;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  console.log('🎨 MediaTextComponent: currentTheme:', currentTheme);
  console.log('🎨 MediaTextComponent: themeStyles.mediaText:', themeStyles.mediaText);
  console.log('🎨 MediaTextComponent: data colors:', {
    backgroundColor: data.backgroundColor,
    textColor: data.textColor,
    titleColor: data.titleColor,
  });

  // Spacing classes
  const spacingClasses = {
    compact: 'py-8 md:py-12',
    normal: 'py-12 md:py-16',
    spacious: 'py-16 md:py-24',
  };

  // Title size classes
  const titleSizeClasses = {
    small: 'text-xl md:text-2xl',
    medium: 'text-2xl md:text-3xl',
    large: 'text-3xl md:text-4xl',
    xl: 'text-4xl md:text-5xl',
  };

  // Border radius classes for media
  const borderRadiusClasses = {
    none: 'rounded-none',
    small: 'rounded-lg',
    medium: 'rounded-xl',
    large: 'rounded-2xl',
    circle: 'rounded-full',
  };

  // Shadow classes for media
  const shadowClasses = {
    small: 'shadow-sm',
    medium: 'shadow-md',
    large: 'shadow-lg',
  };

  // Get button colors - priority: customColors > theme
  const accentColor =
    (data as any).customColors?.buttonBackground || themeStyles.mediaText?.accentColor || '#DC2626';
  const buttonText = (data as any).customColors?.buttonText || '#ffffff';

  // Button style objects (we'll use inline styles for theme colors)
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: accentColor,
          color: buttonText,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: accentColor,
          border: `2px solid ${accentColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: accentColor,
          border: `2px solid ${accentColor}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: accentColor,
          border: 'none',
        };
      default:
        return {
          backgroundColor: accentColor,
          color: buttonText,
          border: 'none',
        };
    }
  };

  // Hover styles for buttons
  const getButtonHoverClass = (style: string) => {
    switch (style) {
      case 'outline':
        return 'hover:opacity-90';
      default:
        return 'hover:opacity-90';
    }
  };

  // Button size classes
  const buttonSizeClasses = {
    small: 'px-4 py-2 text-sm',
    medium: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg',
  };

  // Generate media styles
  const getMediaStyles = () => {
    const styles: React.CSSProperties = {};

    if (data.media.border?.enabled) {
      styles.borderWidth = `${data.media.border.width}px`;
      styles.borderColor = data.media.border.color;
      styles.borderStyle = data.media.border.style;
    }

    return styles;
  };

  const getMediaClasses = (includeAspectRatio = true) => {
    let classes = '';
    const normalizedBorderRadius =
      data.media.type === 'video' && data.media.borderRadius === 'circle'
        ? 'large'
        : data.media.borderRadius;

    if (normalizedBorderRadius) {
      classes += ` ${borderRadiusClasses[normalizedBorderRadius]}`;
    }

    if (data.media.shadow?.enabled && data.media.shadow.size) {
      classes += ` ${shadowClasses[data.media.shadow.size]}`;
    }

    if (includeAspectRatio && data.media.aspectRatio && data.media.aspectRatio !== 'auto') {
      const aspectRatioClasses: Record<string, string> = {
        '16:9': 'aspect-video',
        '4:3': 'aspect-[4/3]',
        '1:1': 'aspect-square',
        '3:2': 'aspect-[3/2]',
        '9:16': 'aspect-[9/16]',
      };
      classes += ` ${aspectRatioClasses[data.media.aspectRatio] || ''}`;
    }

    return classes.trim();
  };

  const videoSourceType = (data.media as any).sourceType || 'upload';
  const videoIsEmbed = isEmbedProvider(videoSourceType);
  const videoUrl = data.media.cloudfrontUrl || data.media.url;
  const videoPoster =
    (data.media as any).thumbnail?.cloudfrontUrl ||
    (data.media as any).thumbnail?.url ||
    data.media.poster;
  // Embeds always use 16:9; uploads use native sizing (no forced ratio).
  const plyrRatio = videoIsEmbed ? EMBED_ASPECT_RATIO : undefined;

  // Memoize Plyr source/options for uploaded videos only (embeds use direct iframes)
  const memoizedVideoSrc = useMemo(() => {
    if (data.media.type !== 'video' || videoIsEmbed) return null;
    return {
      type: 'video' as const,
      sources: [{ src: videoUrl, type: 'video/mp4' }],
      poster: videoPoster,
    };
  }, [data.media.type, videoIsEmbed, videoUrl, videoPoster]);

  const memoizedPlyrOptions = useMemo(() => {
    return {
      controls:
        data.media.controls !== false
          ? ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
          : [],
      autoplay: data.media.autoplay || false,
      muted: data.media.muted !== false,
      loop: { active: data.media.loop || false },
      hideControls: !data.media.controls,
      clickToPlay: true,
      keyboard: { focused: true, global: false },
      tooltips: { controls: true, seek: true },
      storage: { enabled: false },
    };
  }, [data.media.controls, data.media.autoplay, data.media.muted, data.media.loop]);

  // Render media content
  const renderMedia = () => {
    const mediaStyles = getMediaStyles();

    if (data.media.type === 'video') {
      const mediaClasses = getMediaClasses(false);
      const aspectValue = plyrRatio ? plyrRatio.replace(':', '/') : '16/9';

      // For embeds (YouTube/Vimeo), render a direct iframe to avoid plyr-react issues
      if (videoIsEmbed && data.media.url) {
        const embedSrc = isEmbedProvider(videoSourceType)
          ? getEmbedSrc(videoSourceType, data.media.url)
          : null;

        return (
          <div
            className={`
              w-full ${mediaClasses} flex justify-center overflow-hidden
              ${data.alignment === 'top' ? 'items-start' : data.alignment === 'bottom' ? 'items-end' : 'items-center'}
            `}
            style={mediaStyles}
          >
            <div
              className="w-full"
              style={{
                aspectRatio: plyrRatio ? EMBED_ASPECT_RATIO_CSS : aspectValue,
                background: '#000',
              }}
            >
              <iframe
                src={embedSrc || ''}
                className="w-full h-full"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video preview"
              />
            </div>
          </div>
        );
      }

      // For uploaded videos, use Plyr (works fine for native <video> elements)
      if (memoizedVideoSrc) {
        return (
          <div
            className={`
              w-full ${mediaClasses} flex justify-center overflow-hidden
              ${data.alignment === 'top' ? 'items-start' : data.alignment === 'bottom' ? 'items-end' : 'items-center'}
            `}
            style={mediaStyles}
          >
            <div className="w-full">
              <Plyr source={memoizedVideoSrc} options={memoizedPlyrOptions} />
            </div>
          </div>
        );
      }
    }

    const mediaClasses = getMediaClasses();

    // Get crop styles if crop data exists
    const crop = (data.media as any).crop;
    const { hasCrop, aspectRatio, imageStyle } = getCroppedImageRenderProps(crop);

    if (hasCrop) {
      return (
        <div
          className={`${mediaClasses} relative overflow-hidden`}
          style={{
            ...mediaStyles,
            aspectRatio: aspectRatio,
            width: '100%',
          }}
        >
          <img
            src={data.media.cloudfrontUrl || data.media.url}
            alt={data.media.alt || data.title}
            style={imageStyle}
          />
        </div>
      );
    }

    // No crop - render normally (but still apply grayscale if set)
    const baseImageStyle = crop?.grayscale ? { filter: 'grayscale(100%)' } : {};

    return (
      <div className={`${mediaClasses} overflow-hidden`}>
        <img
          src={data.media.cloudfrontUrl || data.media.url}
          alt={data.media.alt || data.title}
          className="w-full h-full object-cover"
          style={{ ...mediaStyles, ...baseImageStyle }}
          width={data.media.width}
          height={data.media.height}
        />
      </div>
    );
  };

  // Generate section styles - PRIORITY: customColors > theme/brand package > stored data
  const sectionStyles: React.CSSProperties = {
    backgroundColor:
      (data as any).customColors?.background ||
      themeStyles.mediaText?.background ||
      data.backgroundColor ||
      '#ffffff',
    paddingTop: `${data.padding.top}px`,
    paddingBottom: `${data.padding.bottom}px`,
    paddingLeft: `${data.padding.left}px`,
    paddingRight: `${data.padding.right}px`,
  };

  // Color priority: component.customColors > deprecated fields > theme colors
  const titleColor =
    (data as any).customColors?.title ||
    themeStyles.mediaText?.titleColor ||
    data.titleColor ||
    '#111827';
  const textColor =
    (data as any).customColors?.text ||
    themeStyles.mediaText?.textColor ||
    data.textColor ||
    '#4b5563';
  const subtitleColor =
    (data as any).customColors?.subtitle ||
    data.subtitleColor ||
    themeStyles.mediaText?.subtitleColor ||
    themeStyles.mediaText?.titleColor ||
    '#111827';

  return (
    <section className={`${spacingClasses[data.spacing]} relative`} style={sectionStyles}>
      <style>
        {`
          @media (min-width: 1024px) {
            .media-text-container-${component.id} {
              min-height: 400px;
            }
            .media-text-media-${component.id} {
              flex-basis: ${data.mediaWidth}%;
              max-width: ${data.mediaWidth}%;
              flex-shrink: 0;
            }
            .media-text-text-${component.id} {
              flex-basis: ${data.textWidth}%;
              max-width: ${data.textWidth}%;
              flex-grow: 1;
            }
          }
        `}
      </style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`
            media-text-container-${component.id}
            flex flex-col lg:flex-row gap-8 lg:gap-12
            ${data.alignment === 'top' ? 'items-start' : data.alignment === 'bottom' ? 'items-end' : 'items-center'}
            ${data.stackOnMobile ? (data.mobileMediaFirst ? 'flex-col' : 'flex-col-reverse') : ''}
            ${data.layout === 'media-right' ? 'lg:flex-row-reverse' : ''}
          `}
        >
          {/* Media Section */}
          <div
            className={`media-text-media-${component.id} w-full flex items-center justify-center`}
          >
            <div className="relative overflow-hidden w-full">{renderMedia()}</div>
          </div>

          {/* Text Content Section */}
          <div
            className={`
              media-text-text-${component.id} w-full flex flex-col justify-center text-left
            `}
          >
            {/* Subtitle */}
            {data.subtitle && (
              <>
                <style>{`
                  .media-text-subtitle-${component.id}, .media-text-subtitle-${component.id} * {
                    color: ${subtitleColor} !important;
                  }
                `}</style>
                <p
                  className={`media-text-subtitle-${component.id} text-sm md:text-base font-medium uppercase tracking-wide mb-4 opacity-80`}
                  style={{ ...bodyFontStyle }}
                  dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
                />
              </>
            )}

            {/* Title */}
            <>
              <style>{`
                .media-text-title-${component.id}, .media-text-title-${component.id} * {
                  color: ${titleColor} !important;
                }
              `}</style>
              <h2
                className={`media-text-title-${component.id} ${titleSizeClasses[data.titleSize || 'large']} font-bold mb-6 leading-tight`}
                style={{ ...headingFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
              />
            </>

            {/* Content */}
            <>
              <style>{`
                .media-text-content-${component.id}, .media-text-content-${component.id} * {
                  color: ${textColor} !important;
                }
              `}</style>
              <div
                className={`media-text-content-${component.id} rich-text-content text-base md:text-lg leading-relaxed mb-8`}
                style={{ ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            </>

            {/* Buttons */}
            {data.buttons.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-4">
                {data.buttons
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((button, index) => (
                    <ButtonLink
                      key={button.id || index}
                      url={button.url}
                      target={button.target}
                      className={`
                        ${getButtonHoverClass(button.style)}
                        ${buttonSizeClasses[button.size]}
                        rounded-lg font-semibold transition-all duration-200
                        transform hover:scale-105 shadow-md hover:shadow-lg
                        inline-block text-center
                      `}
                      style={getButtonStyles(button.style)}
                    >
                      {button.text}
                    </ButtonLink>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animation styles for scroll animations */}
      {data.animateOnScroll && (
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .animate-on-scroll {
              animation: fadeInUp 0.6s ease-out forwards;
              animation-delay: ${data.animationDelay}ms;
            }
          `}
        </style>
      )}
    </section>
  );
};
